/**
 * Property Archive block — editor component.
 *
 * Provides a premium inspector control surface (matching the Elementor
 * "HVN: Property Archive" widget plus native block supports) and a live
 * server-rendered preview via ServerSideRender, so the editor experience mirrors
 * the real frontend output.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	RangeControl,
	ToggleControl,
	TextControl,
	FormTokenField,
	Notice,
} from '@wordpress/components';
import BlockPreview from '../shared/BlockPreview';

const VIEW_OPTIONS = [
	{ label: __( 'Grid', 'havenlytics' ), value: 'grid' },
	{ label: __( 'List', 'havenlytics' ), value: 'list' },
	{ label: __( 'Map', 'havenlytics' ), value: 'map' },
];

const ORDERBY_OPTIONS = [
	{ label: __( 'Newest first', 'havenlytics' ), value: 'date' },
	{ label: __( 'Title (A–Z)', 'havenlytics' ), value: 'title' },
	{ label: __( 'Price: low to high', 'havenlytics' ), value: 'price_low' },
	{ label: __( 'Price: high to low', 'havenlytics' ), value: 'price_high' },
	{ label: __( 'Random', 'havenlytics' ), value: 'rand' },
];

const SIDEBAR_POSITION_OPTIONS = [
	{ label: __( 'Left', 'havenlytics' ), value: 'left' },
	{ label: __( 'Right', 'havenlytics' ), value: 'right' },
];

/**
 * Normalize a FormTokenField value (array of strings) into slugs.
 *
 * @param {Array} tokens Raw tokens from FormTokenField.
 * @return {Array} Trimmed, non-empty tokens.
 */
function cleanTokens( tokens ) {
	if ( ! Array.isArray( tokens ) ) {
		return [];
	}

	return tokens
		.map( ( token ) => String( token ).trim() )
		.filter( ( token ) => token.length > 0 );
}

export default function Edit( { attributes, setAttributes } ) {
	const {
		defaultView,
		columns,
		postsPerPage,
		orderby,
		featuredOnly,
		showTopSearch,
		showFilterSidebar,
		sidebarPosition,
		department,
		propertyType,
		status,
		location,
	} = attributes;

	const blockProps = useBlockProps();

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Layout', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Same archive experience as the Elementor Property Archive widget.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Default view', 'havenlytics' ) }
						value={ defaultView }
						options={ VIEW_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { defaultView: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Columns', 'havenlytics' ) }
						value={ columns }
						min={ 1 }
						max={ 4 }
						onChange={ ( value ) =>
							setAttributes( { columns: value } )
						}
						help={ __( 'Applies to grid view.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show top search bar', 'havenlytics' ) }
						checked={ !! showTopSearch }
						onChange={ ( value ) =>
							setAttributes( { showTopSearch: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show filter sidebar', 'havenlytics' ) }
						checked={ !! showFilterSidebar }
						onChange={ ( value ) =>
							setAttributes( { showFilterSidebar: value } )
						}
						__nextHasNoMarginBottom
					/>
					{ showFilterSidebar && (
						<SelectControl
							label={ __( 'Sidebar position', 'havenlytics' ) }
							value={ sidebarPosition }
							options={ SIDEBAR_POSITION_OPTIONS }
							onChange={ ( value ) =>
								setAttributes( { sidebarPosition: value } )
							}
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				<PanelBody
					title={ __( 'Query', 'havenlytics' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Properties per page', 'havenlytics' ) }
						value={ postsPerPage }
						min={ 1 }
						max={ 48 }
						onChange={ ( value ) =>
							setAttributes( { postsPerPage: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Order by', 'havenlytics' ) }
						value={ orderby }
						options={ ORDERBY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __(
							'Featured properties only',
							'havenlytics'
						) }
						checked={ !! featuredOnly }
						onChange={ ( value ) =>
							setAttributes( { featuredOnly: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Taxonomy filters', 'havenlytics' ) }
					initialOpen={ false }
				>
					<Notice status="info" isDismissible={ false }>
						{ __(
							'Enter term slugs to pre-filter the listing. Visitors can still refine results with the search filters.',
							'havenlytics'
						) }
					</Notice>
					<TextControl
						label={ __( 'Department (slug)', 'havenlytics' ) }
						value={ department }
						onChange={ ( value ) =>
							setAttributes( { department: value } )
						}
						__nextHasNoMarginBottom
					/>
					<FormTokenField
						label={ __( 'Property types (slugs)', 'havenlytics' ) }
						value={ propertyType }
						onChange={ ( tokens ) =>
							setAttributes( {
								propertyType: cleanTokens( tokens ),
							} )
						}
					/>
					<FormTokenField
						label={ __( 'Status (slugs)', 'havenlytics' ) }
						value={ status }
						onChange={ ( tokens ) =>
							setAttributes( { status: cleanTokens( tokens ) } )
						}
					/>
					<FormTokenField
						label={ __( 'Locations (slugs)', 'havenlytics' ) }
						value={ location }
						onChange={ ( tokens ) =>
							setAttributes( { location: cleanTokens( tokens ) } )
						}
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/property-archive"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
